import React from 'react';
import store from './redux/store';
import HttpsRedirect from 'react-https-redirect';
import './index.css';
import './custom.scss';
import Routers from './Routers';
import { ToastContainer, cssTransition } from 'react-toastify';
import { Provider } from 'react-redux';
import { injectStore } from './apiServices/axios';
import 'react-toastify/dist/ReactToastify.css';
import { PersistGate } from 'redux-persist/integration/react';
import { persistStore } from 'redux-persist';
import { FluentProvider, teamsLightTheme } from '@fluentui/react-components';
import { createRoot } from 'react-dom/client';
import { SidebarProvider, PersistentLayout } from './components/Layout';
import { AiWardenProvider } from './components/AiWarden';
import { PulseModalProvider } from './components/Pulse/PulseModalContext';
import PulseModal from './components/Pulse/PulseModal';
import DuplicateTabModal from './components/CommonComponents/DuplicateTabModal';
import AppUpdateModal from './components/CommonComponents/AppUpdateModal';
import DuplicateProjectJobPoller from './components/ProjectDashboard/DuplicateProjectJobPoller';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import TokenService from './apiServices/tokenservices/token.service';
import Cookies from 'js-cookie';
import { useDeferredToast } from './hooks/useDeferredToast';
import useBrowserTimezoneReport from './hooks/useBrowserTimezoneReport';

const lensToastTransition = cssTransition({
    enter: 'lens-toast-enter',
    exit: 'lens-toast-exit',
    collapse: true,
    collapseDuration: 220,
});

// Migrate existing cookies to have secure/sameSite attributes on app initialization
// This ensures old cookies get updated with new security settings
const refreshToken = Cookies.get('refresh');
if (refreshToken) {
    Cookies.set('refresh', refreshToken, {
        secure: true,
        sameSite: 'strict',
    });
}

// Component to conditionally render layout
const AppContent = () => {
    const location = useLocation();
    const { user } = useSelector(state => state.user);
    useDeferredToast(Boolean(user?.id));
    useBrowserTimezoneReport(Boolean(user?.id));

    // Define routes that should NOT have the layout (auth pages, etc.)
    const noLayoutRoutes = [
        '/',
        '/register',
        '/complete-profile',
        '/forgot-password',
        '/confirm-mail',
        '/secure',
        '/setup-2fa',
        '/authenticate',
        '/request-invite',
        '/reset-password',
        '/noprojects',
        '/NDASuccess',
        '/project',
        '/select-workspace',
        '/no-workspace-access',
        '/nda',
        '/user-settings',
        '/uploadnda',
        '/AddProjectDetails',
        '/shared/document',
        '/validate-invite',
    ];

    // Check if current route should have layout
    const shouldHaveLayout = !noLayoutRoutes.some(route => location.pathname === route || location.pathname.startsWith(route + '/')) && user?.id; // Only show layout for authenticated users

    return (
        <PulseModalProvider>
            {shouldHaveLayout ? (
                <PersistentLayout>
                    <Routers />
                </PersistentLayout>
            ) : (
                <Routers />
            )}
            {user?.id ? <DuplicateTabModal /> : null}
            {user?.id ? <AppUpdateModal /> : null}
            {user?.id ? <DuplicateProjectJobPoller /> : null}
            {user?.id ? <PulseModal /> : null}
        </PulseModalProvider>
    );
};

injectStore(store);
let persistor = persistStore(store);
const container = document.getElementById('root');
const root = createRoot(container); // createRoot(container!) if you use TypeScript
root.render(
    <Provider store={store}>
        <HttpsRedirect>
            <PersistGate loading={null} persistor={persistor}>
                <FluentProvider theme={teamsLightTheme}>
                    <Router>
                        <SidebarProvider>
                            <AiWardenProvider>
                                <AppContent />
                            </AiWardenProvider>
                            <ToastContainer
                                position="top-center"
                                autoClose={3000}
                                style={{ zIndex: 1000000 }}
                                hideProgressBar={false}
                                newestOnTop
                                closeOnClick
                                pauseOnFocusLoss
                                draggable
                                pauseOnHover
                                limit={4}
                                theme="light"
                                transition={lensToastTransition}
                                toastClassName="lens-toast"
                                bodyClassName="lens-toast-body"
                                progressClassName="lens-toast-progress"
                            />
                        </SidebarProvider>
                    </Router>
                </FluentProvider>
            </PersistGate>
        </HttpsRedirect>
    </Provider>,
);
